import { createContext, useCallback, useContext, useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { authApi } from '@/features/auth/api/authApi';
import { SessionKeeper } from '@/features/auth/components/SessionKeeper';
import { onAnnouncement } from '@/lib/authSession';
import { tokenStore } from '@/lib/tokenStore';

const SIGNED_OUT_MESSAGES = {
  idle: 'You were signed out after a while with no activity.',
  absolute: 'Your session ended. Please sign in again.',
};

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const queryClient = useQueryClient();

  // /me is the source of truth for identity. The token only says whether it is
  // worth asking - decoding a JWT client-side to trust its claims would mean
  // believing whatever the token says, including a stale or tampered role.
  const {
    data: user,
    isLoading,
    isFetched,
  } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: authApi.me,
    enabled: tokenStore.isAuthenticated(),
    retry: false,
    staleTime: 5 * 60_000,
  });

  const login = useCallback(
    async (credentials) => {
      const result = await authApi.login(credentials);
      // Seed the cache so the very next render already knows who signed in.
      queryClient.setQueryData(['auth', 'me'], result.user);
      return result.user;
    },
    [queryClient],
  );

  const logout = useCallback(async () => {
    await authApi.logout();
    // Reset active query observers to null before clearing the cache
    queryClient.setQueryData(['auth', 'me'], null);
    // Drop every cached query: another user must never see the previous one's data.
    queryClient.clear();
  }, [queryClient]);

  // Dispatched by endSession: a refresh failed for good, or the session ran
  // out (lib/authSession.js).
  useEffect(() => {
    function handleExpiry(event) {
      tokenStore.clear();
      queryClient.setQueryData(['auth', 'me'], null);
      queryClient.clear();
      const message = SIGNED_OUT_MESSAGES[event?.detail?.reason];
      if (message) toast.info(message, { toastId: 'signed-out' });
    }
    window.addEventListener('auth:session-expired', handleExpiry);
    const handleLapse = () => queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
    window.addEventListener('auth:subscription-expired', handleLapse);
    // Another tab signed out or in. Out: follow it. In: possibly as someone
    // else, so this tab's cached data may be another user's - start over.
    const stopListening = onAnnouncement((type) => {
      if (type === 'signed-out') handleExpiry();
      if (type === 'signed-in') window.location.reload();
    });
    return () => {
      window.removeEventListener('auth:session-expired', handleExpiry);
      window.removeEventListener('auth:subscription-expired', handleLapse);
      stopListening();
    };
  }, [queryClient]);

  const value = useMemo(
    () => ({
      user: user ?? null,
      role: user?.role ?? null,
      isAuthenticated: Boolean(user),
      // Only "loading" while a token exists and /me is still in flight;
      // an anonymous visitor should never wait on it.
      isLoading: tokenStore.isAuthenticated() && isLoading && !isFetched,
      login,
      logout,
      hasRole: (...roles) => Boolean(user) && roles.includes(user.role),
    }),
    [user, isLoading, isFetched, login, logout],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
      {user && <SessionKeeper onSignOut={logout} />}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === null) {
    throw new Error('useAuth must be used inside an <AuthProvider>');
  }
  return context;
}
